/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/* istanbul ignore file */
// NOTE!
// This file is being ignored in the test coverage report for now. It reports `0%` functions
// tested, which brings down the overall coverage and blocks CI. There are tests still, but
// we don't want it to count toward coverage until we figure out how to cover the `functions`
// metric for this file in its test.

import React from 'react'

import loadable from '@loadable/component'
import {Redirect} from 'react-router-dom'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

// Components
import {Skeleton} from '@chakra-ui/react'
import {configureRoutes} from './utils/routes-utils'
import useMultiSite from './hooks/use-multi-site'
import {
    HOME_HREF,
    PASSWORDLESS_LOGIN_LANDING_PATH,
    PDP_ROUTE_PATH,
    PLP_ROUTE_PATH,
    PWA_RESET_PASSWORD_ENABLED,
    STORE_LOCATOR_IS_ENABLED,
    RESET_PASSWORD_LANDING_PATH,
    WARRANTY_REGISTER_HREF,
    CPSIA_HREF
} from './constants'
import {resolveSiteFromUrl} from './utils/site-utils'
import {getCorrespondingHomePage} from './utils/url'
import {isServer, resolveLocaleFromUrl} from './utils/utils'
import CustomerServiceSkeleton from './pages/customer-service/skeleton'

const fallback = <Skeleton height="75vh" width="100%" />
const socialRedirectURI = getConfig()?.app?.login?.social?.redirectURI

// Pages
const Home = loadable(() => import('./pages/home'), {fallback})
const Login = loadable(() => import('./pages/login'), {fallback})
const Registration = loadable(() => import('./pages/registration'), {fallback})
const ResetPassword = loadable(() => import('./pages/reset-password'), {fallback})
const CreateNewPassword = loadable(() => import('./pages/create-new-password'), {fallback})
const Cart = loadable(() => import('./pages/cart'), {fallback})
const Checkout = loadable(() => import('./pages/checkout'), {fallback})
const CheckoutConfirmation = loadable(() => import('./pages/checkout/confirmation'), {fallback})
const CustomerService = loadable(() => import('./pages/customer-service'), {
    fallback: CustomerServiceSkeleton
})
const LeapContent = loadable(() => import('./pages/leap-content'), {fallback})
const LoginRedirect = loadable(() => import('./pages/login-redirect'), {fallback})
const MyStokke = loadable(() => import('./pages/my-stokke'), {fallback})
const ProductDetail = loadable(() => import('./pages/product-detail'), {fallback})
const ProductList = loadable(() => import('./pages/product-list'), {fallback})
const CountrySelector = loadable(() => import('./pages/country-selector'))
const PageNotFound = loadable(() => import('./pages/page-not-found'))
const Content = loadable(() => import('./pages/content'), {fallback})
const CheckoutCancellation = loadable(() => import('./pages/checkout/cancellation'), {fallback})
const ShipmentTracking = loadable(() => import('./pages/shipment-tracking'), {fallback})
const ReturnOrder = loadable(() => import('./pages/return-order'), {fallback})
const SocialLoginRedirect = loadable(() => import('./pages/social-login-redirect'), {fallback})
const Momentvm = loadable(() => import('./pages/momentvm'), {fallback})
const StoreLocator = loadable(() => import('./pages/store-locator'), {
    fallback
})
const WarrantyPortal = loadable(() => import('./pages/warranty-portal'), {fallback})
const ExplorationTest = loadable(() => import('./pages/exploration-test'), {fallback})
const UrlMappedRouting = loadable(() => import('./pages/dynamic-routing/url-mapped-routing'), {
    fallback
})

const RedirectToHome = () => {
    // STOK-14304 this functionality won't be executed due to missing accpet-language header
    // instead of that - the SFCC controller would redirect to corresponding home page
    // code would be kept for further investigation
    const {req, res} = useServerContext()
    let correspondingHomePage
    if (req?.path === '/') {
        const language = req.headers?.['accept-language']?.split(';')[0]?.split(',')[0]
        const countryCode = language?.split('-')?.[1]
        correspondingHomePage = getCorrespondingHomePage(countryCode)
        res.set('vary', ['accept-encoding', 'accept-language'].join(', '))
    }
    const {buildUrl} = useMultiSite()
    return <Redirect to={correspondingHomePage || buildUrl(HOME_HREF)} />
}

const ExplorationTestWrapper = (Page) => {
    const page = () => {
        const {app} = getConfig()
        if (!app.explorationTestEnabled) {
            return <RedirectToHome />
        }

        return <Page />
    }
    if (process.env.NODE_ENV === 'development') {
        page.displayName = `ExplorationTestWrapper(${Page.displayName || Page.name})`
    }
    return page
}

const routes = [
    {
        path: '/',
        component: RedirectToHome,
        exact: true
    },
    {
        path: '/home',
        component: Home,
        exact: true
    },
    {
        path: '/login',
        component: Login,
        exact: true
    },
    {
        path: '/registration',
        component: Registration,
        exact: true
    },
    {
        path: '/reset-password',
        component: ResetPassword,
        exact: true
    },
    {
        path: '/exploration/test',
        component: ExplorationTestWrapper(ExplorationTest),
        exact: true
    },
    ...(PWA_RESET_PASSWORD_ENABLED
        ? [
              {
                  path: RESET_PASSWORD_LANDING_PATH,
                  component: ResetPassword,
                  exact: true
              },
              {
                  path: PASSWORDLESS_LOGIN_LANDING_PATH,
                  component: Login,
                  exact: true
              }
          ]
        : []),
    // maybe these routes are not needed when PWA_RESET_PASSWORD_ENABLED is true
    {
        path: '/create-new-password',
        component: CreateNewPassword,
        exact: true
    },
    {
        path: '/set-password',
        component: CreateNewPassword,
        exact: true
    },
    // end of maybe not needed
    {
        path: '/my-stokke',
        component: MyStokke
    },
    // TO BE REMOVED
    {
        path: '/support/:pageId*',
        component: CustomerService
    },
    {
        path: '/customer-service/:pageId*',
        component: CustomerService
    },
    {
        path: '/contact-us',
        component: () => <CustomerService pageId="contact-form" pageType="content" />
    },
    {
        path: CPSIA_HREF,
        component: () => <WarrantyPortal mode="cpsia" />
    },
    {
        path: WARRANTY_REGISTER_HREF,
        component: () => <WarrantyPortal mode="warranty" />
    },
    {
        path: '/appcheckout',
        component: Checkout,
        exact: true
    },
    {
        path: '/appcheckout/confirmation',
        component: CheckoutConfirmation,
        exact: true
    },
    {
        path: '/callback',
        component: LoginRedirect,
        exact: true
    },
    {
        path: socialRedirectURI || '/social-callback',
        component: SocialLoginRedirect,
        exact: true
    },
    {
        path: '/appcart',
        component: Cart,
        exact: true
    },
    {
        path: '/ct/:contentId.leap.html',
        component: ExplorationTestWrapper(LeapContent)
    },
    {
        path: '/(stokke|ct|newsroom)/:contentId.html',
        component: Content
    },
    {
        path: '/(stokke|ct|newsroom)/*/:contentId.html',
        component: Content
    },
    {
        // legacy PWA content urls
        path: '/content/:contentId',
        component: Content
    },
    {
        path: '/search',
        component: ProductList
    },
    {
        path: '/category/:categoryPath+',
        component: UrlMappedRouting
    },
    {
        path: '/country-selector',
        component: CountrySelector
    },
    {
        path: '/cancel-order/:hash',
        component: CheckoutCancellation
    },
    {
        path: PDP_ROUTE_PATH,
        component: ProductDetail
    },
    // legacy product URLs
    {
        path: '/product/:productId',
        component: ProductDetail
    },
    {
        path: '/shipment-tracking',
        component: ShipmentTracking
    },
    {
        path: '/momentvm',
        component: Momentvm
    },
    {
        path: '/return-order',
        component: ReturnOrder
    },
    ...(STORE_LOCATOR_IS_ENABLED
        ? [
              {
                  path: '/stores',
                  component: StoreLocator
              }
          ]
        : []),
    // Dynamic routing - keep this as the second-to-last route
    {
        path: PLP_ROUTE_PATH,
        component: UrlMappedRouting
    },
    // Default route if none of the above matched - keep this as the last route
    {
        path: '*',
        component: PageNotFound
    }
]

export default () => {
    const config = getConfig()
    const currentPath = isServer ? null : `${window.location.pathname}${window.location.search}`
    const options = {
        ignoredRoutes: ['/callback', '*'],
        ...(currentPath
            ? // filter generated routes to the current site and locale on the client
              // change of site or locale requires reload, so we don't need
              // to handle all other sites and locales in the client
              {
                  site: resolveSiteFromUrl(currentPath),
                  locale: resolveLocaleFromUrl(currentPath)
              }
            : {})
    }

    return configureRoutes(routes, config, options)
}
